import React, { useEffect, useRef, useState } from "react";
import { ResponsiveContainer, PieChart, Pie, Cell, Customized } from "recharts";

const RADIAN = Math.PI / 180;
const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

const CustomGuage = ({
  value = 0,
  min = 0,
  max = 100,
  partitions = [],
  ticks = [0, 20, 40, 60, 80, 100],
  showTicks = true,
  showTickLines = true,
  showArcLabels = false,
  height = "100%",
  showInArcVal = true,
  uomPerValue = "",
}) => {
  const [animatedValue, setAnimatedValue] = useState(min);
  const startTimeRef = useRef(null);
  const duration = 1000;

  // Animate needle
  useEffect(() => {
    startTimeRef.current = null;
    const animate = (time) => {
      if (!startTimeRef.current) startTimeRef.current = time;
      const progress = Math.min((time - startTimeRef.current) / duration, 1);
      setAnimatedValue(min + (value - min) * progress);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [value, min]);

  const renderOverlay = ({ width, height: containerHeight }) => {
    const cx = width / 2;
    const cy = containerHeight * 0.9;

    const radius = Math.min(width, containerHeight) * 0.45;
    const outerRadius = radius;
    const innerRadius = outerRadius * 0.6;
    const arcLabelRadius = innerRadius + (outerRadius - innerRadius) / 2;

    const angle = 180 * (1 - (animatedValue - min) / (max - min));
    const needleLength = outerRadius * 0.9;
    const baseWidth = outerRadius * 0.06;

    const sin = Math.sin(-RADIAN * angle);
    const cos = Math.cos(-RADIAN * angle);

    const polarToCartesian = (cx, cy, r, angle) => ({
      x: cx + r * Math.cos(-RADIAN * angle),
      y: cy + r * Math.sin(-RADIAN * angle),
    });

    const describeArc = (startAngle, endAngle) => {
      const start = polarToCartesian(cx, cy, arcLabelRadius, startAngle);
      const end = polarToCartesian(cx, cy, arcLabelRadius, endAngle);
      const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
      return `M ${start.x} ${start.y} A ${arcLabelRadius} ${arcLabelRadius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
    };

    // font size depends on gauge height
    const fontSize = clamp(containerHeight * 0.07, 9, 14);

    let cumulative = min;

    return (
      <g>
        {/* Arc paths */}
        <defs>
          {showArcLabels &&
            partitions.map((p, i) => {
              const start = 180 * (1 - (cumulative - min) / (max - min));
              cumulative += p.value;
              const end = 180 * (1 - (cumulative - min) / (max - min));
              return (
                <path
                  key={i}
                  id={`arcPath-${i}`}
                  d={describeArc(start, end)}
                  fill="none"
                />
              );
            })}
        </defs>

        {/* Arc labels */}
        {showArcLabels &&
          partitions.map((p, i) => (
            <text key={i} fill="#fff" fontSize={fontSize} fontWeight={600}>
              <textPath
                href={`#arcPath-${i}`}
                startOffset="50%"
                textAnchor="middle"
              >
                {p.label}
              </textPath>
            </text>
          ))}

        {/* Ticks */}
        {ticks.map((t) => {
          const a = 180 * (1 - (t - min) / (max - min));
          const tickLength = outerRadius * 0.07;
          const labelRadius = outerRadius * 1.15;

          const start = polarToCartesian(cx, cy, outerRadius, a);
          const end = polarToCartesian(cx, cy, outerRadius + tickLength, a);
          const label = polarToCartesian(cx, cy, labelRadius, a);

          return (
            <g key={t}>
              {showTickLines && (
                <line
                  x1={start.x}
                  y1={start.y}
                  x2={end.x}
                  y2={end.y}
                  stroke="#555"
                  strokeWidth={1}
                />
              )}
              {showTicks && (
                <text
                  x={label.x}
                  y={label.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={fontSize * 0.8}
                  fill="#555"
                >
                  {t}
                </text>
              )}
            </g>
          );
        })}

        {/* Needle */}
        <path
          d={`M ${cx + baseWidth * sin} ${cy - baseWidth * cos} 
              L ${cx - baseWidth * sin} ${cy + baseWidth * cos} 
              L ${cx + needleLength * cos} ${cy + needleLength * sin} Z`}
          fill="#073763"
        />
        <circle cx={cx} cy={cy} r={6} fill="#073763" />

        {/* Value in center of the arc */}
        {showInArcVal && (
          <text
            x={cx} // center horizontally
            y={cy - outerRadius * 0.3} // move up inside the arc (0.3 is adjustable)
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={fontSize + 6} // slightly bigger
            fontWeight={700}
            fill="#073763"
          >
            {Math.round(animatedValue) + uomPerValue}
          </text>
        )}
      </g>
    );
  };

  return (
    <div className="" style={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          {/* Gradients */}
          <defs>
            {partitions
              .filter((p) => p.gradient)
              .map((p) => (
                <linearGradient
                  key={p.gradient.id}
                  id={p.gradient.id}
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor={p.gradient.from} />
                  <stop offset="100%" stopColor={p.gradient.to} />
                </linearGradient>
              ))}
          </defs>

          {/* Gauge Arc */}
          <Pie
            data={partitions}
            dataKey="value"
            startAngle={180}
            endAngle={0}
            cx="50%"
            cy="90%"
            innerRadius="60%"
            outerRadius="90%"
            stroke="none"
            isAnimationActive
            animationDuration={1200}
          >
            {partitions.map((p, i) => (
              <Cell
                key={i}
                fill={p.gradient ? `url(#${p.gradient.id})` : p.color}
              />
            ))}
          </Pie>

          <Customized component={renderOverlay} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CustomGuage;